import DiffMatchPatch from 'diff-match-patch'
import type { Mark, Node as ProseMirrorNode, Schema } from 'prosemirror-model'
import { EditorState } from 'prosemirror-state'

import type { MatterArtifactSelectionAttachment } from '@/agent-v2/artifact-selection/types'
import { MatterArtifactVersionSelectionSchema } from '@/agent-v2/store/schemas'
import { expandPleadingCaptionMarkdownRange } from '../custom_markdown_blocks/pleading-caption'
import { serializeMarkdownDocument } from './editor-helpers'

type CitationRange = {
  citationId: string
  from: number
  to: number
}

type MarkdownDiff = [operation: number, text: string]

type BuildMatterArtifactSelectionResult = {
  attachment: MatterArtifactSelectionAttachment
}

export function buildMatterArtifactSelection(args: {
  document: ProseMirrorNode
  schema: Schema
  sourceMarkdown: string
  matterArtifactId: string
  matterArtifactVersionId: string
  from: number
  to: number
}): BuildMatterArtifactSelectionResult | null {
  const editorRange = expandRangeToCitationBoundaries(args.document, args.from, args.to)
  if (editorRange.from >= editorRange.to) return null

  const serializedMarkdown = serializeMarkdownDocument(args.schema, args.document)
  const startMarker = uniqueMarker(serializedMarkdown, 'PAXTONSELECTIONSTART')
  const endMarker = uniqueMarker(serializedMarkdown, 'PAXTONSELECTIONEND')
  const markerRange = {
    from: captionMarkerPosition(args.document, editorRange.from, 'start') ?? editorRange.from,
    to: captionMarkerPosition(args.document, editorRange.to, 'end') ?? editorRange.to,
  }
  const startMarkerMarks = marksSharedAcrossPosition(args.document, markerRange.from)
  const endMarkerMarks = marksSharedAcrossPosition(args.document, markerRange.to)
  const state = EditorState.create({ doc: args.document, schema: args.schema })
  const markedDocument = state.tr
    .insert(markerRange.to, args.schema.text(endMarker, endMarkerMarks))
    .insert(markerRange.from, args.schema.text(startMarker, startMarkerMarks)).doc
  const markedMarkdown = serializeMarkdownDocument(args.schema, markedDocument)
  const startMarkerIndex = markedMarkdown.indexOf(startMarker)
  const endMarkerIndex = markedMarkdown.indexOf(endMarker)
  if (startMarkerIndex < 0 || endMarkerIndex <= startMarkerIndex) return null

  const markdownWithoutMarkers =
    markedMarkdown.slice(0, startMarkerIndex) +
    markedMarkdown.slice(startMarkerIndex + startMarker.length, endMarkerIndex) +
    markedMarkdown.slice(endMarkerIndex + endMarker.length)
  if (markdownWithoutMarkers !== serializedMarkdown) return null

  const serializedRange = expandPleadingCaptionMarkdownRange(serializedMarkdown, startMarkerIndex, endMarkerIndex - startMarker.length)
  const sourceRange = mapSerializedRangeToSource({
    sourceMarkdown: args.sourceMarkdown,
    serializedMarkdown,
    serializedStartUtf16: serializedRange.startUtf16,
    serializedEndUtf16: serializedRange.endUtf16,
  })
  if (!sourceRange) return null

  const parsedSelection = MatterArtifactVersionSelectionSchema.safeParse({
    matter_artifact_id: args.matterArtifactId,
    matter_artifact_version_id: args.matterArtifactVersionId,
    markdown_start_utf16: sourceRange.startUtf16,
    markdown_end_utf16: sourceRange.endUtf16,
    selected_markdown: args.sourceMarkdown.slice(sourceRange.startUtf16, sourceRange.endUtf16),
  })
  if (!parsedSelection.success) return null

  return {
    attachment: {
      selection: parsedSelection.data,
      editorRange,
    },
  }
}

export function matterArtifactSelectionMatchesDocument(args: {
  attachment: MatterArtifactSelectionAttachment
  document: ProseMirrorNode
  schema: Schema
  sourceMarkdown: string
}): boolean {
  const { selection, editorRange } = args.attachment
  if (editorRange.from < 0 || editorRange.to <= editorRange.from || editorRange.to > args.document.content.size) return false

  const rebuilt = buildMatterArtifactSelection({
    document: args.document,
    schema: args.schema,
    sourceMarkdown: args.sourceMarkdown,
    matterArtifactId: selection.matter_artifact_id,
    matterArtifactVersionId: selection.matter_artifact_version_id,
    from: editorRange.from,
    to: editorRange.to,
  })
  if (!rebuilt) return false

  return (
    rebuilt.attachment.selection.markdown_start_utf16 === selection.markdown_start_utf16 &&
    rebuilt.attachment.selection.markdown_end_utf16 === selection.markdown_end_utf16 &&
    rebuilt.attachment.selection.selected_markdown === selection.selected_markdown
  )
}

function mapSerializedRangeToSource(args: {
  sourceMarkdown: string
  serializedMarkdown: string
  serializedStartUtf16: number
  serializedEndUtf16: number
}): { startUtf16: number; endUtf16: number } | null {
  if (args.sourceMarkdown === args.serializedMarkdown) {
    return {
      startUtf16: args.serializedStartUtf16,
      endUtf16: args.serializedEndUtf16,
    }
  }

  const diffs = new DiffMatchPatch().diff_main(args.serializedMarkdown, args.sourceMarkdown)
  const startUtf16 = mapSerializedBoundaryToSource(diffs, args.serializedStartUtf16, 'start')
  const endUtf16 = mapSerializedBoundaryToSource(diffs, args.serializedEndUtf16, 'end')
  if (startUtf16 === null || endUtf16 === null || startUtf16 >= endUtf16) return null

  return { startUtf16, endUtf16 }
}

// Source-only Markdown at a boundary belongs outside a visible selection: skip it at the
// beginning and stop before it at the end. A boundary inside serializer-only text is not mappable.
function mapSerializedBoundaryToSource(diffs: MarkdownDiff[], offset: number, boundary: 'start' | 'end'): number | null {
  let serializedOffset = 0
  let sourceOffset = 0

  for (const [operation, text] of diffs) {
    if (operation === DiffMatchPatch.DIFF_INSERT) {
      if (serializedOffset === offset && boundary === 'end') return sourceOffset
      sourceOffset += text.length
      continue
    }

    if (operation === DiffMatchPatch.DIFF_DELETE) {
      const nextSerializedOffset = serializedOffset + text.length
      if (offset === serializedOffset) return sourceOffset
      if (offset < nextSerializedOffset) return null
      serializedOffset = nextSerializedOffset
      continue
    }

    const nextSerializedOffset = serializedOffset + text.length
    if (offset >= serializedOffset && offset < nextSerializedOffset) {
      return sourceOffset + offset - serializedOffset
    }
    serializedOffset = nextSerializedOffset
    sourceOffset += text.length
  }

  return offset === serializedOffset ? sourceOffset : null
}

// The table serializer pads cells shorter than three characters, so a marker in a short or empty cell
// removes that padding and the round trip rejects the selection. A boundary anywhere in a caption
// selects the whole caption, so place its marker in the first or last cell long enough to need none.
function captionMarkerPosition(document: ProseMirrorNode, position: number, boundary: 'start' | 'end'): number | null {
  const resolvedPosition = document.resolve(position)
  for (let depth = resolvedPosition.depth; depth > 0; depth--) {
    const caption = resolvedPosition.node(depth)
    if (caption.type.name !== 'pleading_caption') continue

    const contentStart = resolvedPosition.start(depth)
    const unpaddedCells: { from: number; to: number }[] = []
    caption.descendants((node, offset) => {
      if (!node.isTextblock) return
      if (node.textContent.length >= 3) {
        unpaddedCells.push({ from: contentStart + offset + 1, to: contentStart + offset + node.nodeSize - 1 })
      }
      return false
    })
    const cell = boundary === 'start' ? unpaddedCells.at(0) : unpaddedCells.at(-1)
    if (!cell) return null
    return boundary === 'start' ? cell.from : cell.to
  }
  return null
}

function expandRangeToCitationBoundaries(document: ProseMirrorNode, from: number, to: number): { from: number; to: number } {
  let expandedFrom = from
  let expandedTo = to

  for (const citationRange of collectCitationRanges(document)) {
    const intersects = expandedFrom < citationRange.to && expandedTo > citationRange.from
    if (!intersects) continue

    expandedFrom = Math.min(expandedFrom, citationRange.from)
    expandedTo = Math.max(expandedTo, citationRange.to)
  }

  return { from: expandedFrom, to: expandedTo }
}

function collectCitationRanges(document: ProseMirrorNode): CitationRange[] {
  const ranges: CitationRange[] = []

  document.descendants((node, position) => {
    if (node.type.name === 'referenceMarker') {
      ranges.push({
        citationId: String(node.attrs.citationId),
        from: position,
        to: position + node.nodeSize,
      })
      return false
    }
    if (!node.isText) return

    const legalCitation = node.marks.find(isLegalCitationMark)
    if (!legalCitation) return

    const citationId = String(legalCitation.attrs.citationId)
    const previousRange = ranges.at(-1)
    if (previousRange?.citationId === citationId && previousRange.to === position) {
      previousRange.to = position + node.nodeSize
      return
    }
    ranges.push({
      citationId,
      from: position,
      to: position + node.nodeSize,
    })
  })

  return ranges
}

function isLegalCitationMark(mark: Mark): boolean {
  return mark.type.name === 'legalCitation'
}

function marksSharedAcrossPosition(document: ProseMirrorNode, position: number): readonly Mark[] {
  const resolvedPosition = document.resolve(position)
  const nodeBefore = resolvedPosition.nodeBefore
  const nodeAfter = resolvedPosition.nodeAfter
  if (!nodeBefore?.isText || !nodeAfter?.isText) return []

  return nodeBefore.marks.filter((markBefore) => nodeAfter.marks.some((markAfter) => markBefore.eq(markAfter)))
}

function uniqueMarker(markdown: string, base: string): string {
  let marker = base
  while (markdown.includes(marker)) marker += 'X'
  return marker
}
